import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/api/billing'
import { formatNIS } from '@/utils/currency'
import { Loading } from '@/components/ui'

interface Props {
  orgId: string
}

export default function StorageBlock({ orgId }: Props) {
  const { data: row, isLoading } = useQuery({
    queryKey: ['admin-billing-storage', orgId],
    queryFn: () => billingApi.getStorageBilling(orgId),
    staleTime: 60_000,
  })

  const billable = useMemo(() => {
    if (!row) return { gb: 0, agorot: 0 }
    const used = Number(row.used_gb)
    const free = Number(row.free_gb)
    const price = Number(row.price_per_gb_agorot)
    const gb = Math.max(0, used - free)
    return { gb, agorot: Math.round(gb * price) }
  }, [row])

  return (
    <div className="card p-6 border border-navy-border bg-[#1a1d27]">
      <h3 className="text-sm font-semibold text-ink-primary mb-4">Storage (R2)</h3>
      {isLoading ? (
        <Loading text="Loading storage…" />
      ) : !row ? (
        <p className="text-sm text-ink-secondary">
          No storage billing row yet (defaults apply: 5 GB free, ₪0/GB until configured in Admin DB).
        </p>
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-ink-muted">Used</dt>
            <dd className="text-ink-primary font-medium num">{Number(row.used_gb).toFixed(2)} GB</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Free tier</dt>
            <dd className="text-ink-primary font-medium num">{row.free_gb} GB</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Price / GB (mo)</dt>
            <dd className="text-ink-primary font-medium num">{formatNIS(row.price_per_gb_agorot)}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Billable this month</dt>
            <dd className="text-ink-primary font-medium num">
              {billable.gb.toFixed(2)} GB · {formatNIS(billable.agorot)}
            </dd>
          </div>
        </dl>
      )}
    </div>
  )
}
